/**
 * Circuit standings for the Cars list's Pos/Gap cells.
 *
 * MVP semantics: rank every device that has completed at least one lap
 * by its BEST lap; gap = best-lap delta to the leader. That's "who is
 * quickest today", not a live race position — honest and computable
 * from data we already store (`/events/<id>/laps`). Live track-position
 * standings (same-chainage gaps) arrive with the GpsTiming live-engine
 * port and will replace this map without touching consumers.
 *
 * Live pages merge `lap_completed` SSE frames so a fresh personal best
 * reshuffles the list within a second of the crossing.
 */

import { useEffect, useMemo, useState } from 'react';

import api from '../../services/api';
import { Lap, listEventLaps } from '../../services/fleet';
import { FleetEvent } from '../../types';

export interface Standing {
  /** 1-based rank by best lap. */
  pos: number;
  bestMs: number;
  /** Best-lap delta to the leader's best; 0 for the leader. */
  gapMs: number;
}

export function useEventStandings(
  eventId: number | null,
  /** Circuit events only — rally has no lap standings (yet). */
  enabled: boolean,
  /** Subscribe to lap_completed SSE (live pages only). */
  live: boolean,
): Map<string, Standing> {
  const [laps, setLaps] = useState<Lap[]>([]);

  useEffect(() => {
    setLaps([]);
    if (!enabled || eventId == null) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await listEventLaps(eventId); // all devices
        if (!cancelled) setLaps(data);
      } catch (err) {
        console.warn('[useEventStandings] fetch failed', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId, enabled]);

  useEffect(() => {
    if (!enabled || !live || eventId == null) return;
    const unsub = api.subscribeFleetEvents((evt: FleetEvent) => {
      if (evt.type !== 'lap_completed' || evt.event_id !== eventId) return;
      setLaps((prev) => {
        if (
          prev.some(
            (l) => l.session_id === evt.session_id && l.lap_number === evt.lap_number,
          )
        ) {
          return prev;
        }
        return [
          ...prev,
          {
            lap_number: evt.lap_number,
            event_id: evt.event_id,
            device_id: evt.device_id,
            session_id: evt.session_id,
            start_utc_ms: evt.start_utc_ms,
            end_utc_ms: evt.end_utc_ms,
            duration_ms: evt.duration_ms,
            end_lat: null,
            end_lon: null,
            source: 'live',
            sigma_ms: null,
            confidence: null,
            method: null,
            chord_delta_ms: null,
          },
        ];
      });
    });
    return () => unsub();
  }, [enabled, live, eventId]);

  return useMemo(() => {
    const bestByDevice = new Map<string, number>();
    for (const lap of laps) {
      // Skip the out-lap (lap_number 0): a partial, not comparable for
      // best-lap standings.
      if (lap.lap_number < 1) continue;
      const prev = bestByDevice.get(lap.device_id);
      if (prev == null || lap.duration_ms < prev) {
        bestByDevice.set(lap.device_id, lap.duration_ms);
      }
    }
    const ranked = [...bestByDevice.entries()].sort((a, b) => a[1] - b[1]);
    const out = new Map<string, Standing>();
    const leaderMs = ranked[0]?.[1];
    ranked.forEach(([deviceId, bestMs], i) => {
      out.set(deviceId, { pos: i + 1, bestMs, gapMs: bestMs - (leaderMs ?? bestMs) });
    });
    return out;
  }, [laps]);
}

export default useEventStandings;
